import Dexie, { type EntityTable } from 'dexie';
import type { OutboxMutation } from './types';

/**
 * Instancia Dexie del outbox cliente-side.
 *
 * Diseño:
 *   - **Un único store `mutations`** indexado por `id` (PK = UUID v4).
 *   - Índices secundarios: `status`, `userId`, `createdAt`,
 *     `[userId+status]` (compuesto) para que `listPending(userId)` use
 *     un único index range scan sin filtrar en JS.
 *   - **Migraciones por `version()`**: cuando cambiemos el schema en el
 *     futuro, añadir un nuevo `db.version(N).stores(...).upgrade(...)`.
 *
 * No exponemos la instancia Dexie directamente al resto del código; el
 * wrapper `outbox.ts` ofrece una API estable (enqueue, listPending,
 * markDone…) que el sync engine y los hooks consumen.
 *
 * El módulo solo se inicializa en el navegador. Si se carga en SSR
 * (Next.js Server Components), las llamadas Dexie devolverían un error;
 * los call-sites garantizan estar en client component (`'use client'`).
 */

class OutboxDatabase extends Dexie {
  mutations!: EntityTable<OutboxMutation, 'id'>;

  constructor() {
    super('geo-outbox');
    // v1: schema inicial. Mantener compatibilidad eternamente — bumps
    // futuros solo añaden índices, no cambian la PK.
    this.version(1).stores({
      mutations: 'id, status, userId, createdAt, [userId+status]',
    });
  }
}

// Singleton perezoso para que `import './outbox.db'` no cree la conexión
// hasta que algo la use (importante para SSR safety y para tests
// `fake-indexeddb` que setean `globalThis.indexedDB` en `beforeAll`).
let _db: OutboxDatabase | null = null;

export function getOutboxDb(): OutboxDatabase {
  if (!_db) _db = new OutboxDatabase();
  return _db;
}

/**
 * Cierra la conexión Dexie. Útil en tests para limpiar entre cases sin
 * heredar handles abiertos.
 */
export function closeOutboxDb(): void {
  if (_db) {
    _db.close();
    _db = null;
  }
}
